import React from 'react';

export interface StatusBadgeProps {
  status?: string;
  className?: string;
}

type StatusConfig = {
  color: string;
  label?: string; // optional override label
};

const STATUS_MAP: Record<string, StatusConfig> = {
  // ── Generic / Store ────────────────────────────────
  online: { color: 'bg-green-100 text-green-700', label: 'Online' },
  yes: { color: 'bg-green-100 text-green-700', label: 'Yes' },
  no: { color: 'bg-red-100   text-red-700', label: 'No' },
  active: { color: 'bg-green-100 text-green-700', label: 'Active' },
  deleted: { color: 'bg-rose-100 text-rose-700', label: 'Deleted' },
  offline: { color: 'bg-red-100   text-red-700', label: 'Offline' },
  inactive: { color: 'bg-red-100   text-red-700', label: 'Inactive' },
  busy: { color: 'bg-amber-100 text-amber-700', label: 'Busy' },

  // ── Order: Initial ────────────────────────────────────
  pending: { color: 'bg-blue-100  text-blue-700', label: 'Pending' },
  accepted: { color: 'bg-indigo-100 text-indigo-700', label: 'Accepted' },

  // ── Order: Store Progress ───────────────────────────
  in_progress: { color: 'bg-violet-100 text-violet-700', label: 'Preparing' },
  ready_for_pickup: { color: 'bg-cyan-100  text-cyan-700', label: 'Ready' },

  // ── Order: Delivery ───────────────────────────────────
  driver_assigned: { color: 'bg-sky-100   text-sky-700', label: 'Driver Assigned' },
  out_for_delivery: { color: 'bg-sky-100   text-sky-700', label: 'Out for Delivery' },
  arrived: { color: 'bg-teal-100  text-teal-700', label: 'Arrived' },
  destination_arrived: { color: 'bg-teal-100  text-teal-700', label: 'Destination Arrived' },
  picked_up: { color: 'bg-teal-100  text-teal-700', label: 'Picked Up' },

  // ── Order: Terminal / Success ─────────────────────────
  delivered: { color: 'bg-green-100 text-green-700', label: 'Delivered' },
  completed: { color: 'bg-green-100 text-green-700', label: 'Completed' },

  // ── Order: Cancelled / Failed ─────────────────────────
  cancelled: { color: 'bg-red-100   text-red-700', label: 'Cancelled' },
  failed: { color: 'bg-red-100   text-red-700', label: 'Failed' },
  delayed: { color: 'bg-orange-100 text-orange-700', label: 'Delayed' },

  // ── Order: Refund Flow ────────────────────────────────
  refund_requested: { color: 'bg-amber-100 text-amber-700', label: 'Refund Requested' },
  refund_in_progress: { color: 'bg-amber-100 text-amber-700', label: 'Refund In Progress' },
  refunded: { color: 'bg-purple-100 text-purple-700', label: 'Refunded' },
  refund_rejected: { color: 'bg-red-100   text-red-700', label: 'Refund Rejected' },

  // ── Misc ──────────────────────────────────────────────
  created: { color: 'bg-blue-100  text-blue-700', label: 'Created' },
  processing: { color: 'bg-blue-100  text-blue-700', label: 'Processing' },
  success: { color: 'bg-green-100 text-green-700', label: 'Success' },
  error: { color: 'bg-red-100   text-red-700', label: 'Error' },
  draft: { color: 'bg-slate-100 text-slate-700', label: 'Draft' },
  submitted: { color: 'bg-blue-100 text-blue-700', label: 'Submitted' },
  approved: { color: 'bg-green-100 text-green-700', label: 'Approved' },
  rejected: { color: 'bg-red-100   text-red-700', label: 'Rejected' },
  warning: { color: 'bg-amber-100 text-amber-700', label: 'Warning' },

  // ── Verified ──────────────────────────────────────────────
  verified: { color: 'bg-green-100 text-green-700', label: 'Verified' },
  not_verified: { color: 'bg-red-100   text-red-700', label: 'Not Verified' },

  // ── Review Target Types ─────────────────────────────────
  on_product: { color: 'bg-blue-100 text-blue-700', label: 'Product' },
  on_store: { color: 'bg-purple-100 text-purple-700', label: 'Store' },
  on_driver: { color: 'bg-emerald-100 text-emerald-700', label: 'Driver' },
  on_order: { color: 'bg-orange-100 text-orange-700', label: 'Order' },
  // ── Return Orders ─────────────────────────────────────────
  pending_review: { color: 'bg-amber-100 text-amber-700', label: 'Pending Review' },
  more_details_required: { color: 'bg-orange-100 text-orange-700', label: 'More Details' },
  pickup_assigned: { color: 'bg-sky-100 text-sky-700', label: 'Pickup Assigned' },
  re_route_to_customer: { color: 'bg-indigo-100 text-indigo-700', label: 'Re-route to Customer' },
  re_arrived_at_customer: { color: 'bg-violet-100 text-violet-700', label: 'Arrived at Customer' },
  return_collected: { color: 'bg-blue-100 text-blue-700', label: 'Return Collected' },
  re_route_to_store: { color: 'bg-sky-100 text-sky-700', label: 'Re-route to Store' },
  re_arrived_at_store: { color: 'bg-cyan-100 text-cyan-700', label: 'Arrived at Store' },
  return_delivered: { color: 'bg-green-100 text-green-700', label: 'Return Delivered' },
  pickup_and_verify: { color: 'bg-indigo-100 text-indigo-700', label: 'Pickup and Verify' },
  instant_refund: { color: 'bg-purple-100 text-purple-700', label: 'Instant Refund' },
  settled: { color: 'bg-green-100 text-green-700', label: 'Settled' }
};


const FALLBACK: StatusConfig = {
  color: 'bg-gray-100 text-gray-600',
};

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status = 'offline', className = '' }) => {
  const key = status.toLowerCase().trim();
  const config = STATUS_MAP[key] ?? FALLBACK;

  // Use mapped label or format the raw string (replace _ with space, capitalize first letter)
  const label =
    config.label ??
    status.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold tracking-wide whitespace-nowrap ${config.color} ${className}`}
    >
      {label}
    </span>
  );
};
